// Wave F (April 2026) — pure helpers for the ZoneLayout view-
// preference shape. Extracted from useViewPreference.ts so unit
// tests can import these helpers WITHOUT also pulling in
// `../api` (which brings DOM-specific fetch types into scope and
// breaks the root tsconfig that targets Workers types).

export interface ZoneLayout {
  zones: Array<{ id: string; visible: boolean }>;
}

export function isZoneLayout(value: unknown): value is ZoneLayout {
  if (typeof value !== "object" || value === null) return false;
  const v = value as { zones?: unknown };
  if (!Array.isArray(v.zones)) return false;
  for (const z of v.zones) {
    if (typeof z !== "object" || z === null) return false;
    const zo = z as { id?: unknown; visible?: unknown };
    if (typeof zo.id !== "string") return false;
    if (typeof zo.visible !== "boolean") return false;
  }
  return true;
}

export function parseZoneLayout(json: string): ZoneLayout | null {
  try {
    const parsed: unknown = JSON.parse(json);
    return isZoneLayout(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Reconciles a saved layout against the current set of available
 * zones (defined by the rendering component). Drops zones that no
 * longer exist; appends new zones at the end with visible=true.
 * This way new zones added to a page automatically appear for
 * users who already have a saved layout.
 */
export function reconcileZoneLayout(
  saved: ZoneLayout,
  availableZoneIds: readonly string[],
): ZoneLayout {
  const seen = new Set<string>();
  const out: Array<{ id: string; visible: boolean }> = [];
  for (const z of saved.zones) {
    if (availableZoneIds.includes(z.id) && !seen.has(z.id)) {
      out.push({ id: z.id, visible: z.visible });
      seen.add(z.id);
    }
  }
  for (const id of availableZoneIds) {
    if (!seen.has(id)) {
      out.push({ id, visible: true });
    }
  }
  return { zones: out };
}
